import React, { useState, useRef, DragEvent, useEffect } from "react";
import {
  Trash2,
  Settings,
  Type,
  Mail,
  Phone,
  Calendar,
  Hash,
  List,
  FileText,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  CircleDot,
} from "lucide-react";
import { Button, DatePicker, Modal, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { UploadFile } from "antd";
import { createForm, fetchFormById, updateForm } from "../../app/formslice";
import { toast } from "react-toastify";
import { AppDispatch } from "../../app/store";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchSurveyDropdownList } from "../../services/surveyServices";

// Types
interface BaseField {
  id: string;
  type: string;
  label: string;
  is_required: boolean;
  placeholder?: string | null;
  validation_message?: string;
  description?: string | null; // ✅ Add this
  default_value?: string | null;
  rows?: number;
  multiple_select?: boolean;
  options?: OptionItem[];
}
interface OptionItem {
  label: string;
  value: string;
  selected?: boolean;
}
interface Survey {
  id: number;
  name?: string;
}
interface TextField extends BaseField {
  type: "text" | "email" | "tel" | "number" | "label";
}
interface TextareaField extends BaseField {
  type: "textarea";
  rows?: number;
}

interface SelectField extends BaseField {
  type: "select";
  options: OptionItem[];
}
interface TimeField extends BaseField {
  type: "time";
  defaultValue?: dayjs.Dayjs;
}
interface CheckboxField extends BaseField {
  type: "checkbox";
  options: OptionItem[];
}

interface DateField extends BaseField {
  type: "date";
  defaultValue?: dayjs.Dayjs;
}

interface FileField extends BaseField {
  type: "file";
  multiple_select?: boolean;
  fileList?: UploadFile[]; // <-- for controlled Upload
}

interface RadioField extends BaseField {
  type: "radio";
  options: OptionItem[];
}

type FormField =
  | TextField
  | TextareaField
  | SelectField
  | CheckboxField
  | DateField
  | RadioField
  | FileField
  | TimeField;
interface FieldTemplate {
  type: string;
  label: string;
  icon: React.ReactNode;
  defaultProps: any;
}

// Field templates
const fieldTemplates: FieldTemplate[] = [
  {
    type: "label",
    label: "Label",
    icon: <Hash className="w-4 h-4" />,
    defaultProps: {
      description: "",
    },
  },
  {
    type: "text",
    label: "Text Input",
    icon: <Type className="w-4 h-4" />,
    defaultProps: {
      placeholder: "Enter text...",
      description: "Enter your text here",
    },
  },
  {
    type: "email",
    label: "Email",
    icon: <Mail className="w-4 h-4" />,
    defaultProps: {
      placeholder: "Enter email...",
      description: "Enter your email address",
    },
  },
  {
    type: "tel",
    label: "Phone",
    icon: <Phone className="w-4 h-4" />,
    defaultProps: {
      placeholder: "Enter phone...",
      description: "Enter your phone number",
    },
  },
  {
    type: "number",
    label: "Number",
    icon: <Hash className="w-4 h-4" />,
    defaultProps: {
      placeholder: "Enter number...",
      description: "Enter a numeric value",
    },
  },
  {
    type: "date",
    label: "Date",
    icon: <Calendar className="w-4 h-4" />,
    defaultProps: {
      description: "Select a date",
      defaultValue: dayjs(),
      format: "DD-MM-YYYY",
    },
  },
  {
    type: "textarea",
    label: "Textarea",
    icon: <FileText className="w-4 h-4" />,
    defaultProps: {
      placeholder: "Enter text...",
      rows: 3,
      description: "Enter detailed text or message",
    },
  },
  {
    type: "select",
    label: "Dropdown",
    icon: <List className="w-4 h-4" />,
    defaultProps: {
      description: "Select an option from the list",
      options: [
        { label: "Option 1", value: "option-1", selected: false },
        { label: "Option 2", value: "option-2", selected: false },
      ],
    },
  },
  {
    type: "time",
    label: "Time Picker",
    icon: <Calendar className="w-4 h-4" />,
    defaultProps: {
      description: "Select a time",
      defaultValue: dayjs(),
    },
  },
  {
    type: "checkbox",
    label: "Checkbox",
    icon: <CheckSquare className="w-4 h-4" />,
    defaultProps: {
      description: "Select one or more options",
      options: [
        { label: "Option 1", value: "option-1", selected: false },
        { label: "Option 2", value: "option-2", selected: false },
      ],
    },
  },
  {
    type: "file",
    label: "File Upload",
    icon: <Type className="w-4 h-4" />,
    defaultProps: {
      description: "Upload a file",
    },
  },
  {
    type: "radio",
    label: "Radio Group",
    icon: <CircleDot className="w-4 h-4" />,
    defaultProps: {
      description: "Select one option",
      options: [
        { label: "Option 1", value: "option-1", selected: false },
        { label: "Option 2", value: "option-2", selected: false },
      ],
    },
  },
];
// Components
const DraggableFieldTemplate: React.FC<{
  template: FieldTemplate;
  onDragStart: (template: FieldTemplate) => void;
}> = ({ template, onDragStart }) => {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(template)}
      className="flex items-center gap-2 p-3 bg-white dark:bg-black border border-gray-200 rounded cursor-move hover:bg-gray-50 hover:border-blue-300 transition-colors"
    >
      {template.icon}
      <span className="text-sm font-medium">{template.label}</span>
    </div>
  );
};

const FieldRenderer: React.FC<{
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onFieldUpdate: (updatedField: FormField) => void; // ← Add this
}> = ({
  field,
  isSelected,
  onSelect,
  onDelete,
  onFieldUpdate,
  onMoveDown,
  onMoveUp,
}) => {
  const renderField = () => {
    switch (field.type) {
      case "text":
      case "email":
      case "tel":
      case "number":
        return (
          <input
            type={field.type}
            placeholder={field.placeholder || ""}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-black"
            disabled
          />
        );

      case "date":
        return (
          <DatePicker
            className="w-full"
            format="DD-MM-YYYY"
            placeholder="Select date"
            value={(field as DateField).defaultValue || undefined}
            onChange={(value) => {
              const updatedField: DateField = {
                ...(field as DateField),
                defaultValue: value || undefined,
              };
              onFieldUpdate(updatedField); // ← update the field value in state
            }}
          />
        );

      case "file":
        return (
          <Upload
            multiple={field.multiple_select}
            fileList={field.fileList || []}
            beforeUpload={() => false}
            onChange={(info) => {
              const newFileList = [...info.fileList];
              const updatedField: FileField = {
                ...field,
                fileList: newFileList,
              };
              onFieldUpdate(updatedField);
            }}
            onRemove={(file) => {
              const newFileList = (field.fileList || []).filter(
                (f) => f.uid !== file.uid
              );
              const updatedField: FileField = {
                ...field,
                fileList: newFileList,
              };
              onFieldUpdate(updatedField);
              return false; // prevents auto removal (we handle it manually)
            }}
          >
            <Button icon={<UploadOutlined />}>
              {field.multiple_select ? "Upload Files" : "Upload File"}
            </Button>
          </Upload>
        );
      case "time":
        return (
          <DatePicker
            className="w-full"
            picker="time"
            format="hh:mm A" // ✅ 12-hour format
            use12Hours
            value={(field as TimeField).defaultValue || undefined}
            onChange={(value) => {
              const updatedField: TimeField = {
                ...(field as TimeField),
                defaultValue: value || undefined,
              };
              onFieldUpdate(updatedField);
            }}
          />
        );

      case "textarea":
        return (
          <textarea
            placeholder={field.placeholder || ""}
            rows={(field as TextareaField).rows || 0}
            value={field.default_value || ""}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            disabled
          />
        );
      case "select":
        return (
          <select disabled className="...">
            {(field as SelectField).options.map((opt, i) => (
              <option key={i} value={opt.value} selected={opt.selected}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case "radio":
        return (
          <div className="space-y-2">
            {(field as RadioField).options.map((opt, i) => (
              <label key={i} className="flex items-center gap-2">
                <input type="radio" checked={opt.selected} disabled />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <div className="space-y-2">
            {(field as CheckboxField).options.map((opt, i) => (
              <label key={i} className="flex items-center gap-2">
                <input type="checkbox" checked={opt.selected} disabled />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      onClick={onSelect}
      className={`relative p-4 rounded-xl border-2 shadow-sm transition-all cursor-pointer group ${
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:border-blue-300 hover:shadow-md bg-white"
      }`}
    >
      {/* Top Toolbar */}
      <div className="flex justify-between items-start mb-2">
        <label className="block text-base font-semibold text-gray-800">
          {field.label}
          {field.is_required && <span className="text-red-600 ml-1">*</span>}
        </label>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp();
            }}
            title="Move Up"
            className="p-1 rounded-full hover:bg-green-100 transition"
          >
            <ChevronUp className="w-4 h-4 text-green-500" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown();
            }}
            title="Move Down"
            className="p-1 rounded-full hover:bg-yellow-100 transition"
          >
            <ChevronDown className="w-4 h-4 text-yellow-500" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Delete"
            className="p-1 rounded-full hover:bg-red-100 transition"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      {/* Field Preview */}
      <div>{renderField()}</div>
    </div>
  );
};

const FieldEditor: React.FC<{
  field: FormField | null;
  onUpdate: (field: FormField) => void;
}> = ({ field, onUpdate }) => {
  if (!field) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        Select a field to edit its properties
      </div>
    );
  }

  const handleLabelChange = (label: string) => {
    onUpdate({ ...field, label });
  };

  const handleRequiredChange = (is_required: boolean) => {
    onUpdate({ ...field, is_required });
  };

  const handlePlaceholderChange = (placeholder: string) => {
    if ("placeholder" in field) {
      onUpdate({ ...field, placeholder });
    }
  };
  const handleDescriptionChange = (description: string) => {
    if ("description" in field) {
      onUpdate({ ...field, description });
    }
  };

  const handleRowsChange = (rows: number) => {
    if (field.type === "textarea") {
      onUpdate({ ...field, rows });
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="w-5 h-5" />
        <h3 className="font-semibold">Field Properties</h3>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-white  mb-1">
          Label
        </label>
        <input
          type="text"
          value={field.label}
          onChange={(e) => handleLabelChange(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {field.type !== "label" && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={field.is_required}
            onChange={(e) => handleRequiredChange(e.target.checked)}
            className="w-4 h-4"
          />
          <label className="text-sm font-medium text-gray-700 dark:text-white">
            Required field
          </label>
        </div>
      )}

      {"placeholder" in field && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
            Placeholder
          </label>
          <input
            type="text"
            value={field.placeholder || ""}
            onChange={(e) => handlePlaceholderChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
      {"description" in field && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
            Description
          </label>
          <input
            type="text"
            value={field.description || ""}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {field.type === "time" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
            Default Time
          </label>
          <DatePicker
            picker="time"
            format="hh:mm A"
            use12Hours
            className="w-full"
            value={(field as TimeField).defaultValue || undefined}
            onChange={(value) => {
              const updated = {
                ...(field as TimeField),
                defaultValue: value || undefined,
              };
              onUpdate(updated);
            }}
          />
        </div>
      )}

      {field.type === "file" && (
        <div className="flex items-center gap-2">
          <input
            className="h-4 w-4"
            type="checkbox"
            checked={(field as FileField).multiple_select || false}
            onChange={(e) =>
              onUpdate({
                ...(field as FileField),
                multiple_select: e.target.checked,
              })
            }
          />
          <label className="text-sm font-medium text-gray-700 dark:text-white">
            Allow multiple files
          </label>
        </div>
      )}

      {field.type === "textarea" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
            Rows
          </label>
          <input
            type="number"
            min="1"
            max="10"
            value={(field as TextareaField).rows || 3}
            onChange={(e) => handleRowsChange(parseInt(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {["select", "radio", "checkbox"].includes(field.type) && (
        <div>
          <label className="text-sm font-medium">Options</label>
          {(field as SelectField | RadioField | CheckboxField).options.map(
            (opt, index) => (
              <div key={index} className="flex gap-2 mb-2 items-center">
                <input
                  type="text"
                  placeholder="Label"
                  value={opt.label}
                  onChange={(e) => {
                    const updatedOptions = [
                      ...(field as SelectField | RadioField | CheckboxField)
                        .options,
                    ];
                    updatedOptions[index] = {
                      ...updatedOptions[index],
                      label: e.target.value,
                    };
                    onUpdate({
                      ...field,
                      options: updatedOptions,
                    });
                  }}
                  className="p-1 border rounded w-[40%]"
                />
                <input
                  type="text"
                  placeholder="Value"
                  value={opt.value}
                  onChange={(e) => {
                    const updatedOptions = [
                      ...(field as SelectField | RadioField | CheckboxField)
                        .options,
                    ];
                    updatedOptions[index] = {
                      ...updatedOptions[index],
                      value: e.target.value,
                    };
                    onUpdate({
                      ...field,
                      options: updatedOptions,
                    });
                  }}
                  className="p-1 border rounded w-[40%]"
                />
                <input
                  type="checkbox"
                  checked={opt.selected}
                  onChange={(e) => {
                    const updatedOptions = [
                      ...(field as SelectField | RadioField | CheckboxField)
                        .options,
                    ];
                    updatedOptions[index] = {
                      ...updatedOptions[index],
                      selected: e.target.checked,
                    };
                    onUpdate({
                      ...field,
                      options: updatedOptions,
                    });
                  }}
                />
                <button
                  onClick={() => {
                    const updatedOptions = (
                      field as SelectField | RadioField | CheckboxField
                    ).options.filter((_, i) => i !== index);

                    onUpdate({
                      ...field,
                      options: updatedOptions,
                    });
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  ×
                </button>
              </div>
            )
          )}

          <button
            onClick={() => {
              const currentOptions = (
                field as SelectField | RadioField | CheckboxField
              ).options;
              const updatedOptions = [
                ...currentOptions,
                {
                  label: `Option ${currentOptions.length + 1}`,
                  value: `option-${currentOptions.length + 1}`,
                  selected: false,
                },
              ];
              onUpdate({
                ...field,
                options: updatedOptions,
              });
            }}
            className="text-blue-600 hover:underline mt-2"
          >
            + Add Option
          </button>
        </div>
      )}
    </div>
  );
};

// Main Form Builder Component
const FormBuilder: React.FC = () => {
  const location = useLocation();
  const { survey_version_id, survey_id, form_available } = location.state || {};
  const [fields, setFields] = useState<FormField[]>([]);
  const [surveyList, setSurveyList] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>("");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [draggedTemplate, setDraggedTemplate] = useState<FieldTemplate | null>(
    null
  );
  const navigate = useNavigate();
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formJson, setFormJson] = useState<string>("");
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dropPosition, setDropPosition] = useState<"before" | "after" | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const generateId = () =>
    `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  useEffect(() => {
    const loadSurvey = async () => {
      try {
        const result = await fetchSurveyDropdownList(
          null,
          true,
          (form_available ?? 0) > 0 ? survey_id : null
        );
        setSurveyList(result);
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch surveys");
      }
    };

    loadSurvey();
  }, []);
  useEffect(() => {
    if (survey_id) {
      setSelectedSurveyId(survey_id || 0); // make sure it's string
    }
  }, [survey_id]);
  useEffect(() => {
    const fetchFormData = async () => {
      const versionId = survey_version_id ?? "";
      const formId = survey_id ?? "";
      if (!versionId || !formId) {
        setFields([]);
        // setSelectedSurveyId("");
        setSelectedFieldId(null);
        return;
      }

      try {
        setLoading(true);
        const result = await dispatch(
          fetchFormById({
            survey_version_id: Number(versionId),
            survey_id: Number(formId),
          })
        ).unwrap();
        const mappedFields: FormField[] = result.data.map((item: any) => ({
          id: `field_${item.id}`,
          type: item.type,
          label: item.label,
          is_required: item.is_required,
          default_value: item.default_value,
          multiple_select: item.multiple_select,
          placeholder: item.placeholder,
          rows: item.rows,
          description: item.description,
          validation_message: item.validation_message,
          options: item.options || [],
        }));

        setFields(mappedFields);
        setSelectedSurveyId(formId);
      } catch (error: any) {
        toast.error(error || "Failed to fetch form data");
      } finally {
        setLoading(false);
      }
    };
    fetchFormData();
  }, [survey_version_id ?? "", survey_id ?? ""]);

  const handleDragStart = (template: FieldTemplate) => {
    setDraggedTemplate(template);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!draggedTemplate) return;
    setIsDragging(true);
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY - rect.top + container.scrollTop;
    const fieldElements = container.querySelectorAll("[data-field-id]");
    if (fieldElements.length === 0) {
      setDragOverIndex(0);
      setDropPosition("before");
      return;
    }
    let targetIndex = 0;
    let position: "before" | "after" = "before";
    for (let i = 0; i < fieldElements.length; i++) {
      const element = fieldElements[i];
      const elementRect = element.getBoundingClientRect();
      const elementTop = elementRect.top - rect.top + container.scrollTop;
      const elementBottom = elementRect.bottom - rect.top + container.scrollTop;
      const elementCenter = elementTop + elementRect.height / 2;
      if (mouseY < elementCenter) {
        targetIndex = i;
        position = "before";
        break;
      } else {
        targetIndex = i;
        position = "after";
      }
    }
    setDragOverIndex(targetIndex);
    setDropPosition(position);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!draggedTemplate) return;

    const newField: FormField = {
      id: generateId(),
      type: draggedTemplate.type,
      label: draggedTemplate.label,
      is_required: false,
      ...draggedTemplate.defaultProps,
    };
    // ✅ Deep clone options if the type supports it
    if (["select", "radio", "checkbox"].includes(draggedTemplate.type)) {
      newField.options =
        draggedTemplate.defaultProps?.options?.map((opt: any) => ({
          ...opt, // deep clone each option object
        })) || [];
    }

    setFields((prev) => {
      if (prev.length === 0) return [newField];
      const insertIndex =
        dragOverIndex === null
          ? prev.length
          : dropPosition === "after"
          ? dragOverIndex + 1
          : dragOverIndex;

      const newFields = [...prev];
      newFields.splice(
        Math.max(0, Math.min(insertIndex, newFields.length)),
        0,
        newField
      );
      return newFields;
    });
    // Reset states with animation
    setDraggedTemplate(null);
    setSelectedFieldId(newField.id);
    resetDragStates();
    // Auto-scroll to new field
    setTimeout(() => {
      document
        .querySelector(`[data-field-id="${newField.id}"]`)
        ?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
    }, 150);
  };

  // Clean drag leave handler
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      resetDragStates();
    }
  };

  // Helper to reset all drag states
  const resetDragStates = () => {
    setDragOverIndex(null);
    setDropPosition(null);
    setIsDragging(false);
  };

  // Magnetic Drop Zone Component
  const DropZone = ({
    position,
    index,
    label,
  }: {
    position: "top" | "bottom";
    index: number;
    label?: string;
  }) => (
    <div
      className={`
    absolute left-0 right-0 z-20 transition-all duration-300 ease-out
    ${position === "top" ? "-top-4 -translate-y-2" : "-bottom-4 translate-y-2"}
  `}
    >
      <div className="relative mx-4">
        {/* Glowing background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-blue-400/20 rounded-lg blur-sm" />

        {/* Main drop zone */}
        <div className="relative h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg border border-blue-300/50 flex items-center justify-center group hover:shadow-xl transition-all duration-200">
          {/* Animated dots */}
          <div className="flex space-x-1">
            <div
              className="w-1 h-1 bg-white/80 rounded-full animate-pulse"
              style={{ animationDelay: "0ms" }}
            />
            <div
              className="w-1 h-1 bg-white/80 rounded-full animate-pulse"
              style={{ animationDelay: "150ms" }}
            />
            <div
              className="w-1 h-1 bg-white/80 rounded-full animate-pulse"
              style={{ animationDelay: "300ms" }}
            />
          </div>

          {/* Drop text */}
          <span className="ml-3 text-xs font-semibold text-white/90 group-hover:text-white transition-colors">
            {label || "Drop here"}
          </span>

          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000" />
        </div>
      </div>
    </div>
  );
  const moveFieldUp = (id: string) => {
    const index = fields.findIndex((f) => f.id === id);
    if (index > 0) {
      const newFields = [...fields];
      [newFields[index - 1], newFields[index]] = [
        newFields[index],
        newFields[index - 1],
      ];
      setFields(newFields);
    }
  };

  const moveFieldDown = (id: string) => {
    const index = fields.findIndex((f) => f.id === id);
    if (index < fields.length - 1) {
      const newFields = [...fields];
      [newFields[index], newFields[index + 1]] = [
        newFields[index + 1],
        newFields[index],
      ];
      setFields(newFields);
    }
  };

  const handleFieldSelect = (fieldId: string) => {
    setSelectedFieldId(fieldId);
  };

  const handleFieldUpdate = (updatedField: FormField) => {
    setFields((prev) =>
      prev.map((field) => (field.id === updatedField.id ? updatedField : field))
    );
  };

  const handleFieldDelete = (fieldId: string) => {
    setFields((prev) => prev.filter((field) => field.id !== fieldId));
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
  };

  const selectedField =
    fields.find((field) => field.id === selectedFieldId) || null;

  const handleClearAll = () => {
    setFields([]);
    setSelectedFieldId(null);
  };
  const handleSave = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedSurveyId) {
      newErrors.survey_id = "Survey is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true); // start loading

    const cleanedFields = fields.map(({ id, ...rest }) => {
      const validation_message = rest.is_required
        ? `${rest.label || "This field"} is required`
        : undefined;

      return {
        ...rest,
        ...(validation_message && { validation_message }),
      };
    });

    const formData = new FormData();
    formData.append("form_data", JSON.stringify(cleanedFields));

    // Append IDs
    if (survey_version_id && survey_id) {
      // UPDATE mode
      formData.append("survey_id", survey_id);
      formData.append("survey_version_id", survey_version_id);
    } else {
      // CREATE mode
      formData.append("survey_id", selectedSurveyId);
    }

    try {
      const result = await dispatch(
        survey_version_id && survey_id
          ? updateForm(formData)
          : createForm(formData)
      ).unwrap();
      toast.success(result.message || "Form saved successfully!");
      navigate("/dashboard/surveyListing");
    } catch (error: any) {
      toast.error(error || "Failed to save form");
    } finally {
      setLoading(false); // stop loading
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="bg-white dark:bg-black border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Form Builder
          </h1>
          <div className="relative w-1/2">
            <select
              name="survey_id"
              value={selectedSurveyId}
              onChange={(e) => {
                setSelectedSurveyId(e.target.value);
                setErrors((prev) => ({ ...prev, survey_id: "" }));
              }}
              className={`appearance-none w-full rounded-lg border py-2 px-4 pr-10 dark:bg-form-input dark:text-white ${
                errors.survey_id
                  ? "border-red-500"
                  : "border-stroke dark:border-form-strokedark"
              }`}
            >
              <option value="" disabled hidden>-- Select Survey --</option>
              {surveyList.map((survey) => (
                <option key={survey.id} value={survey.id}>
                  {survey.name}
                </option>
              ))}
            </select>

            {/* Dropdown Icon */}
            <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Error Message */}
            {errors.survey_id && (
              <p className="text-red-500 text-sm mt-1">{errors.survey_id}</p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={loading || fields.length === 0}
              className={`flex items-center justify-center px-4 py-2 ${
                loading || fields.length === 0
                  ? "opacity-70 cursor-not-allowed bg-green-600"
                  : "bg-green-600 hover:bg-green-700"
              } text-white rounded transition`}
            >
              {loading ? (
                <>
                  <span>
                    {survey_version_id && survey_id ? "Updating" : "Saving"}
                  </span>
                  <span className="h-4 w-4 ml-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                </>
              ) : (
                <span>
                  {survey_version_id && survey_id ? "Update" : "Save"}
                </span>
              )}
            </button>

            <button
              onClick={handleClearAll}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Sidebar - Field Templates */}
        <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Form Elements
          </h2>
          <div className="space-y-2">
            {fieldTemplates.map((template) => (
              <DraggableFieldTemplate
                key={template.type}
                template={template}
                onDragStart={handleDragStart}
              />
            ))}
          </div>
        </div>

        {/* Center - Form Canvas */}
        <div ref={dropZoneRef} className="flex-1 p-6 overflow-y-auto">
          <div className="bg-white dark:bg-black rounded-lg shadow-sm min-h-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Form Preview
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                Drag elements from the left panel to build your form
              </p>
            </div>

            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragLeave={handleDragLeave}
              className={`
          p-6 flex-1 min-h-[60vh] relative transition-all duration-300 ease-out
          ${
            isDragging
              ? "bg-gradient-to-br from-blue-50/80 via-purple-50/40 to-blue-50/80 dark:from-blue-900/20 dark:via-purple-900/10 dark:to-blue-900/20"
              : "bg-gradient-to-br from-gray-50/30 to-white dark:from-gray-900 dark:to-gray-800"
          }
        `}
            >
              {/* Animated border overlay when dragging */}
              {isDragging && (
                <div className="absolute inset-4 border-2 border-dashed border-blue-400 dark:border-blue-500 rounded-xl bg-gradient-to-br from-blue-100/30 to-purple-100/20 dark:from-blue-900/30 dark:to-purple-900/20 transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-transparent to-purple-400/10 animate-pulse rounded-xl" />
                </div>
              )}

              {/* Empty State */}
              {fields.length === 0 ? (
                <div className="flex items-center justify-center h-full relative">
                  <div
                    className={`
              text-center p-12 rounded-2xl border-2 border-dashed transition-all duration-300 transform
              ${
                isDragging
                  ? "border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-100/50 to-purple-100/30 dark:from-blue-900/30 dark:to-purple-900/20 scale-105 shadow-2xl"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white/50 dark:bg-gray-800/50"
              }
            `}
                  >
                    {/* Floating elements animation */}
                    {isDragging && (
                      <div className="absolute inset-0 overflow-hidden rounded-2xl">
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i}
                            className={`absolute w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-60`}
                            style={{
                              left: `${20 + i * 12}%`,
                              top: `${30 + (i % 2) * 40}%`,
                              animation: `float 3s ease-in-out infinite ${
                                i * 0.5
                              }s`,
                            }}
                          />
                        ))}
                      </div>
                    )}

                    <div
                      className={`transition-all duration-300 ${
                        isDragging ? "transform -translate-y-2" : ""
                      }`}
                    >
                      <div className="text-6xl mb-4 opacity-50">
                        {isDragging ? "🎯" : "📝"}
                      </div>
                      <div className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">
                        {isDragging
                          ? "Drop it like it's hot!"
                          : "Start Building Your Form"}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                        {isDragging
                          ? "Release to add your field here"
                          : "Drag components from the sidebar"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Fields Container */
                <div className="space-y-6 relative">
                  {/* Top drop zone */}
                  {dragOverIndex === 0 && dropPosition === "before" && (
                    <DropZone position="top" index={0} label="Add to top" />
                  )}

                  {/* Fields with enhanced styling */}
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      data-field-id={field.id}
                      className={`
                  relative transition-all duration-300 ease-out group
                  ${
                    dragOverIndex === index
                      ? dropPosition === "before"
                        ? "transform translate-y-6"
                        : dropPosition === "after"
                        ? "transform -translate-y-6"
                        : ""
                      : ""
                  }
                `}
                    >
                      {/* Drop zone before field (not first) */}
                      {dragOverIndex === index &&
                        dropPosition === "before" &&
                        index > 0 && <DropZone position="top" index={index} />}

                      {/* Field wrapper with hover effects */}
                      <div
                        className={`
                  relative bg-white dark:bg-gray-800 rounded-xl border transition-all duration-200
                  ${
                    selectedFieldId === field.id
                      ? "border-blue-500 dark:border-blue-400 shadow-lg shadow-blue-500/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
                  }
                  group-hover:shadow-lg group-hover:scale-[1.02]
                `}
                      >
                        <FieldRenderer
                          field={field}
                          isSelected={selectedFieldId === field.id}
                          onSelect={() => handleFieldSelect(field.id)}
                          onDelete={() => handleFieldDelete(field.id)}
                          onFieldUpdate={handleFieldUpdate}
                          onMoveUp={() => moveFieldUp(field.id)}
                          onMoveDown={() => moveFieldDown(field.id)}
                        />
                      </div>
                      {/* Drop zone after field */}
                      {dragOverIndex === index && dropPosition === "after" && (
                        <DropZone position="bottom" index={index} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Field Editor */}
        <div className="w-80 bg-white dark:bg-black border-l border-gray-200">
          <FieldEditor field={selectedField} onUpdate={handleFieldUpdate} />
        </div>
      </div>
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        title="Form JSON Preview"
        width={700}
      >
        <pre className="max-h-[60vh] overflow-auto bg-gray-100 p-4 rounded text-sm">
          {formJson}
        </pre>
      </Modal>
    </div>
  );
};

export default FormBuilder;
